import React, { useState } from 'react';
import { Button } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useModal } from 'mui-modal-provider';
import { useNavigate } from 'react-router-dom';

import CommentAndStatusUpdate from '../../../Posts/Shared/CommentAndStatusUpdate';
import AnalyticsService from '../../../../Services/AnalyticsService';
import { replyBtnTextId } from '../../../Posts/PostSidePrimaryActions/utils';
import EmojiIcon from '../../../DesignSystem/CounterButton/EmojiIcon';
import { validateCanComment } from '../../../../Utils/Post/securedStrategies';
import { isIdea } from '../../../../Utils/Post';
import useSpace from '../../../../Hooks/useSpace';
import useCurrentUser from '../../../../Hooks/useCurrentUser';
import EntitySubscriptionModal from '../../../EntitySubscriptionModal';
import { loginPath } from '../../../../Utils/routes';
import { Post } from '../../../../Utils/Types/Posts';

interface CommentsCreationFormProps {
  post: Post;
  ctaVariant?: string;
  buttonFullWidth?: boolean;
  openModalForStorybook?: boolean;
}

function CommentsCreationForm({
  post,
  ctaVariant = 'contained',
  buttonFullWidth = false,
  openModalForStorybook = false,
}: CommentsCreationFormProps) {
  const intl = useIntl();
  const { space } = useSpace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(openModalForStorybook);
  const { isLoggedIn } = useCurrentUser();

  const { showModal } = useModal();
  const parent = post.included.source;

  const handleModalOpen = (trigger: any) => {
    if (!isLoggedIn) {
      navigate(loginPath());
      return;
    }

    if (!space.entity_subscription) {
      showModal(EntitySubscriptionModal);
      return;
    }

    if (isIdea(parent.type)) {
      openCommentModal(trigger);
    } else {
      validateCanComment(parent, async () => {
        openCommentModal(trigger);
      });
    }
  };

  const openCommentModal = (trigger: any) => {
    AnalyticsService.track('Comment', 'Modal - open', `Comment Modal opened with ${trigger}`, {
      'Post ID': post.id,
      'Post Type': post.type,
    });
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        variant={ctaVariant}
        color="primary"
        size="large"
        startIcon={<EmojiIcon reaction="comment" />}
        onClick={() => handleModalOpen('Comment Button')}
        fullWidth={buttonFullWidth}
        aria-label={intl.formatMessage({ id: replyBtnTextId(post) })}
      >
        <FormattedMessage id={replyBtnTextId(post)} />
      </Button>
      <CommentAndStatusUpdate
        open={open}
        posts={[post]}
        onClose={handleModalClose}
        onSubmit={handleModalClose}
      />
    </>
  );
}

export default CommentsCreationForm;
